import network from './network.js';
import Toast from './toast.js';

interface ScannerData {
	symbol: string;
	timeframe: string;
	news?: any[];
	indicators: {
		ema50: number;
		ema200: number;
		adx: number;
		sar: number;
		rsi: number;
		atr: number;
		cci: number;
	};
	scores: {
		trend: number;
		momentum: number;
		volatility: number;
		strength: number;
		adjustment: number;
	};
	totalScore: number;
	confidence: number;
	timestamp: string;
}

class Scanner {
	private currentTF: string = 'M15';
	private viewMode: 'cards' | 'heatmap' = 'cards';
	private rawData: ScannerData[] = [];
	private filterConviction: boolean = false;
	private refreshInterval: any = null;

	constructor() {
		this.currentTF = localStorage.getItem('lastScannerTF') || 'M15';
		this.viewMode = (localStorage.getItem('lastScannerView') as any) || 'cards';
	}

	public async init() {
		if (!document.getElementById('scanner-grid')) { return; }
		
		this.setupEventListeners();
		this.renderTFSelector();
		await this.loadData();
		
		this.startCountdown();
	}

	private startCountdown() {
		if (this.refreshInterval) { clearInterval(this.refreshInterval); }

		const updateTimer = () => {
			const now = new Date();
			const minutes = now.getMinutes();
			const seconds = now.getSeconds();

			// Calculate minutes remaining to next 15-min mark (00, 15, 30, 45)
			const nextMark = Math.ceil((minutes + 1) / 15) * 15;
			let minRem = nextMark - minutes - 1;
			let secRem = 60 - seconds;

			if (secRem === 60) {
				secRem = 0;
				minRem++;
			}

			// If we just hit a mark (00:00), trigger refresh
			if (minutes % 15 === 0 && seconds === 5) { // 5 second buffer for EA to finish upload
				this.loadData();
			}

			const timerEl = document.getElementById('next-scan-timer');
			if (timerEl) {
				timerEl.innerText = `Next Sync: ${minRem.toString().padStart(2, '0')}:${secRem.toString().padStart(2, '0')}`;
			}
		};

		updateTimer();
		this.refreshInterval = setInterval(updateTimer, 1000);
	}

	private setupEventListeners() {
		document.getElementById('view-cards')?.addEventListener('click', () => this.switchView('cards'));
		document.getElementById('view-heatmap')?.addEventListener('click', () => this.switchView('heatmap'));
		
		document.getElementById('scanner-sort')?.addEventListener('change', () => {
			this.sortAndRender();
		});

		document.getElementById('filter-confidence')?.addEventListener('click', (e) => {
			this.filterConviction = !this.filterConviction;
			(e.target as HTMLElement).classList.toggle('active', this.filterConviction);
			this.sortAndRender();
		});

		// Close overlay on click outside
		document.getElementById('scanner-overlay')?.addEventListener('click', (e) => {
			if (e.target === e.currentTarget) { this.closeExpanded(); }
		});
	}

	private renderTFSelector() {
		const container = document.getElementById('tf-selector-container');
		if (!container) { return; }

		const timeframes = ['M15', 'M30', 'H1', 'H2', 'H3', 'H4', 'H6', 'H8', 'H12', 'D1'];
		const tfSelector = document.createElement('div');
		tfSelector.className = 'tf-selector';
		
		timeframes.forEach(tf => {
			const btn = document.createElement('button');
			btn.className = `tf-btn ${tf === this.currentTF ? 'active' : ''}`;
			btn.innerText = tf;
			btn.onclick = () => this.switchTimeframe(tf);
			tfSelector.appendChild(btn);
		});

		container.innerHTML = '';
		container.appendChild(tfSelector);
	}

	private async switchTimeframe(tf: string) {
		this.currentTF = tf;
		localStorage.setItem('lastScannerTF', tf);
		this.renderTFSelector();
		await this.loadData();
	}

	private switchView(mode: 'cards' | 'heatmap') {
		this.viewMode = mode;
		localStorage.setItem('lastScannerView', mode);
		document.getElementById('view-cards')?.classList.toggle('active', mode === 'cards');
		document.getElementById('view-heatmap')?.classList.toggle('active', mode === 'heatmap');
		document.getElementById('scanner-grid')?.classList.toggle('heatmap-mode', mode === 'heatmap');
		this.sortAndRender();
	}

	private async loadData() {
		try {
			const response = await network.ap('/api/scanner/data', { tf: this.currentTF });
			
			if (response.ok && response.payload.status === 'success') {
				this.rawData = response.payload.data;
				this.updateSummary();
				this.sortAndRender();
			} else if (!response.ok) {
				Toast.show('Failed to load scanner data', 'error');
			}
		} catch (e) {
			Toast.show('Network error occurred', 'error');
		}
	}

	private updateSummary() {
		const bull = this.rawData.filter(d => d.totalScore > 3).length;
		const bear = this.rawData.filter(d => d.totalScore < -3).length;
		const neutral = this.rawData.length - (bull + bear);

		const bullEl = document.getElementById('bull-count');
		const bearEl = document.getElementById('bear-count');
		const neutEl = document.getElementById('neutral-count');
		if (bullEl) { bullEl.innerText = bull.toString(); }
		if (bearEl) { bearEl.innerText = bear.toString(); }
		if (neutEl) { neutEl.innerText = neutral.toString(); }

		const lastUp = document.getElementById('last-updated-time');
		
		if (lastUp && this.rawData.length > 0) {
			const date = new Date(this.rawData[0].timestamp);
			lastUp.innerText = `Last Scan: ${date.toLocaleTimeString()}`;
		}
	}

	private sortAndRender() {
		const sortVal = (document.getElementById('scanner-sort') as HTMLSelectElement)?.value || 'totalScore-desc';
		const [field, order] = sortVal.split('-');

		let filtered = [...this.rawData];
		if (this.filterConviction) {
			filtered = filtered.filter(d => d.confidence > 0.7);
		}

		filtered.sort((a: any, b: any) => {
			const valA = field === 'totalScore' ? a.totalScore : (field === 'confidence' ? a.confidence : a.symbol);
			const valB = field === 'totalScore' ? b.totalScore : (field === 'confidence' ? b.confidence : b.symbol);
			
			if (valA < valB) { return order === 'asc' ? -1 : 1; }
			if (valA > valB) { return order === 'asc' ? 1 : -1; }
			return 0;
		});

		this.renderGrid(filtered);
	}

	private renderGrid(data: ScannerData[] = []) {
		const grid = document.getElementById('scanner-grid');
		if (!grid) { return; }
		
		grid.innerHTML = '';
		if (data.length === 0) {
			grid.innerHTML = '<div class="empty-state">No matching data found for this timeframe.</div>';
			return;
		}
		
		data.forEach(item => {
			const card = document.createElement('div');
			const sentiment = item.totalScore > 3 ? 'BULLISH' : (item.totalScore < -3 ? 'BEARISH' : 'NEUTRAL');
			
			let intensityClass = '';
			
			if (sentiment === 'BULLISH') {
				const level = item.totalScore >= 5 ? 3 : (item.totalScore >= 4 ? 2 : 1);
				intensityClass = `intensity-bull-${level}`;
			} else if (sentiment === 'BEARISH') {
				const level = item.totalScore <= -5 ? 3 : (item.totalScore <= -4 ? 2 : 1);
				intensityClass = `intensity-bear-${level}`;
			}

			card.className = `symbol-card ${intensityClass} ${item.confidence > 0.8 ? 'high-confidence' : ''}`;
			const arrow = item.totalScore > 0 ? '↑' : (item.totalScore < 0 ? '↓' : '—');

			card.innerHTML = `
				<div class="card-top">
					<div class="symbol-name">
						${item.symbol}
						${this.renderNewsIndicators(item.news)}
					</div>
				</div>

				<div class="tot-display" data-tooltip="${this.getAnalystNote('TOT', item.totalScore, item.symbol, false)}">
					<div class="tot-number ${this.getValClass(item.totalScore)}">
						<span class="tot-arrow">${arrow}</span> ${item.totalScore.toFixed(1)}
					</div>
				</div>

				<div class="compact-stats">
					${this.renderMiniStat('TRND', item.scores.trend, 3, this.getTrendIcon(), item.symbol)}
					${this.renderMiniStat('MOM', item.scores.momentum, 3, this.getMomIcon(), item.symbol)}
					${this.renderMiniStat('STR', item.scores.strength, 2, this.getStrIcon(), item.symbol)}
					${this.renderMiniStat('VOL', item.scores.volatility, 1, this.getVolIcon(), item.symbol)}
				</div>

				<div class="confidence-label" style="display: flex; justify-content: space-between; font-size: 0.65rem; color: #64748b; margin-top: 5px;">
					<span>CONVICTION</span>
					<span>${(item.confidence * 100).toFixed(0)}%</span>
				</div>
				<div class="confidence-bar">
					<div class="confidence-fill" style="width: ${item.confidence * 100}%"></div>
				</div>
			`;

			card.onclick = () => this.expandCard(item);
			grid.appendChild(card);
		});
	}

	private expandCard(item: ScannerData) {
		const overlay = document.getElementById('scanner-overlay');
		const content = document.getElementById('expanded-content');
		if (!overlay || !content) { return; }

		const arrow = item.totalScore > 0 ? '↑' : (item.totalScore < 0 ? '↓' : '—');

		content.innerHTML = `
			<button class="close-overlay" id="close-expanded">&times;</button>
			<div style="text-align: center;">
				<h2 style="margin: 0; font-size: 2rem;">${item.symbol}</h2>
				<div style="color: #64748b; font-weight: 600; margin-bottom: 20px;">${item.timeframe} DATA</div>
				
				<div class="tot-number ${this.getValClass(item.totalScore)}" style="font-size: 4rem;" data-tooltip="${this.getAnalystNote('TOT', item.totalScore, item.symbol, true)}">
					<span class="tot-arrow">${arrow}</span> ${item.totalScore.toFixed(1)}
				</div>
				<div style="font-weight: 800; color: #94a3b8; letter-spacing: 0.1em;">NET SIGNAL</div>
			</div>

			${item.news && item.news.length > 0 ? `
				<div style="margin-top: 30px;">
					<div class="detail-label" style="margin-bottom: 10px;">Upcoming News (24h)</div>
					<div style="display: flex; flex-direction: column; gap: 8px;">
						${item.news.map((n: any) => `
							<div style="display: flex; align-items: center; gap: 10px; background: #1e293b; padding: 8px 12px; border-radius: 8px; font-size: 0.85rem;">
								<div class="news-dot impact-${n.impact}"></div>
								<div style="font-weight: 700; width: 60px;">${new Date(n.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
								<div style="flex-grow: 1;">${n.title}</div>
								<div style="color: #94a3b8; font-size: 0.75rem;">${n.country}</div>
							</div>
						`).join('')}
					</div>
				</div>
			` : ''}

			<div class="details-grid">
				<div class="detail-item" data-tooltip="${this.getAnalystNote('TRND', item.scores.trend, item.symbol, true)}">
					<div class="detail-label">Trend Score</div>
					<div class="detail-value ${this.getValClass(item.scores.trend)}">${item.scores.trend}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('MOM', item.scores.momentum, item.symbol, true)}">
					<div class="detail-label">Momentum Score</div>
					<div class="detail-value ${this.getValClass(item.scores.momentum)}">${item.scores.momentum}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('STR', item.scores.strength, item.symbol, true)}">
					<div class="detail-label">Strength Score</div>
					<div class="detail-value ${this.getValClass(item.scores.strength)}">${item.scores.strength}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('VOL', item.scores.volatility, item.symbol, true)}">
					<div class="detail-label">Volatility Score</div>
					<div class="detail-value ${this.getValClass(item.scores.volatility)}">${item.scores.volatility}</div>
				</div>
				
				<div class="detail-item" data-tooltip="${this.getAnalystNote('RSI', item.indicators.rsi, item.symbol, true)}">
					<div class="detail-label">RSI (14)</div>
					<div class="detail-value">${item.indicators.rsi.toFixed(2)}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('ADX', item.indicators.adx, item.symbol, true)}">
					<div class="detail-label">ADX (14)</div>
					<div class="detail-value">${item.indicators.adx.toFixed(2)}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('EMA50', item.indicators.ema50, item.symbol, true)}">
					<div class="detail-label">EMA 50</div>
					<div class="detail-value">${item.indicators.ema50.toFixed(5)}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('EMA200', item.indicators.ema200, item.symbol, true)}">
					<div class="detail-label">EMA 200</div>
					<div class="detail-value">${item.indicators.ema200.toFixed(5)}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('SAR', item.indicators.sar, item.symbol, true)}">
					<div class="detail-label">Parabolic SAR</div>
					<div class="detail-value">${item.indicators.sar.toFixed(5)}</div>
				</div>
				<div class="detail-item" data-tooltip="${this.getAnalystNote('ATR', item.indicators.atr, item.symbol, true)}">
					<div class="detail-label">ATR (14)</div>
					<div class="detail-value">${item.indicators.atr.toFixed(5)}</div>
				</div>
			</div>

			<div style="margin-top: 30px; text-align: center; color: #64748b; font-size: 0.8rem;">
				Last Scan: ${new Date(item.timestamp).toLocaleString()}
			</div>
		`;

		overlay.classList.add('open');
		document.getElementById('close-expanded')?.addEventListener('click', () => this.closeExpanded());
	}

	private closeExpanded() {
		document.getElementById('scanner-overlay')?.classList.remove('open');
	}

	private renderNewsIndicators(news?: any[]) {
		if (!news || news.length === 0) { return ''; }
		
		const sorted = [...news].sort((a, b) => {
			const priority: any = { 'High': 3, 'Medium': 2, 'Low': 1, 'Non-Economic': 0 };
			return priority[b.impact] - priority[a.impact];
		});

		const topEvents = sorted.slice(0, 3);
		const tooltip = news.map(n => `${n.country} ${n.impact}: ${n.title}`).join('\n');

		return `
			<div class="news-indicator" data-tooltip="${tooltip}">
				${topEvents.map(n => `<div class="news-dot impact-${n.impact}"></div>`).join('')}
			</div>
		`;
	}

	private getAnalystNote(type: string, value: number, symbol: string, detailed: boolean): string {
		switch (type) {
			case 'TOT':
				if (value > 4) { return detailed ? `Exceptionally strong bullish net reading. Aggregated scores show overwhelming alignment across trend, momentum, and supporting factors — one of the cleanest bullish setups in the scanner right now. High conviction for ${symbol} continuing to rally.` : `Very strong bullish conviction. Technicals heavily favor upside momentum in ${symbol}.`; }
				if (value > 0) { return detailed ? `Positive net reading. Technicals are aligning for a bullish move in ${symbol}. Buyer control is evident, though standard risk management is advised.` : `Bullish bias. Technicals starting to align for upside.`; }
				if (value < -4) { return detailed ? `Exceptionally strong bearish net reading. Aggregated scores show overwhelming alignment across trend, momentum, and supporting factors. High conviction for ${symbol} continuing to decline.` : `Very strong bearish conviction. Technicals heavily favor downside momentum in ${symbol}.`; }
				if (value < 0) { return detailed ? `Negative net reading. Technicals are aligning for a bearish move in ${symbol}. Seller control is evident.` : `Bearish bias. Technicals starting to align for downside.`; }
				return "Neutral. No clear directional edge detected at this time.";
			case 'TRND':
				if (value > 1.5) { return "Clear uptrend in play. Price action, moving averages, and directional indicators all point strongly higher."; }
				if (value < -1.5) { return "Clear downtrend in play. Price action and moving averages point strongly lower."; }
				return "Trend neutral. Indicators are conflicted or ranging.";
			case 'MOM':
				if (value > 1.5) { return "Momentum is accelerating positively. Buyers are in control with building speed — supports continuation."; }
				if (value < -1.5) { return "Momentum is accelerating negatively. Sellers are in control with building speed."; }
				return "Momentum neutral. No significant acceleration in either direction.";
			case 'STR':
				if (value > 0.5) { return "Positive strength edge. This pair is outperforming its peers or relative flows."; }
				if (value < -0.5) { return "Negative strength edge. This pair is underperforming its peers."; }
				return "Neutral strength. No significant relative edge detected.";
			case 'VOL':
				if (value > 0.4) { return "Moderate volatility expansion. Enough movement to make trades viable without excessive whipsaw."; }
				return "Low volatility or choppy conditions. Risk of false breakouts or compression.";
			case 'RSI':
				if (value > 70) { return "Overbought zone. Momentum is extreme — risk of exhaustion or pullback is elevated."; }
				if (value < 30) { return "Oversold zone. Momentum is extreme — risk of relief rally is elevated."; }
				if (value > 50) { return "Neutral-to-bullish zone. Momentum is healthy — room for further upside."; }
				return "Neutral-to-bearish zone. Momentum is weak — room for further downside.";
			case 'ADX':
				if (value > 25) { return "Solid trend strength. Above 25 confirms the move is directional and worth trading."; }
				return "Weak trend strength. Market may be ranging or the current move is losing steam.";
			case 'EMA50': return "Acts as dynamic support/resistance. Price distance from this average confirms short-term trend bias.";
			case 'EMA200': return "Major underlying structural level. Provides context for the long-term 'Golden' or 'Death' cross structure.";
			case 'SAR': return "Trailing stop indicator. Position relative to price confirms trend direction and protection levels.";
			case 'ATR': return "Average True Range. Provides context for stop/target sizing based on average market movement.";
			default: return "";
		}
	}

	private renderMiniStat(label: string, value: number, max: number, icon: string, symbol: string) {
		const pct = Math.min(100, (Math.abs(value) / max) * 100);
		const color = value > 0 ? '#10b981' : (value < 0 ? '#ef4444' : '#94a3b8');
		const note = this.getAnalystNote(label, value, symbol, false);
		
		return `
			<div class="mini-stat" data-tooltip="${note}">
				<div class="stat-header">${icon} ${label}</div>
				<div class="mini-bar-bg">
					<div class="mini-bar-fill" style="width: ${pct}%; background: ${color}"></div>
				</div>
			</div>
		`;
	}

	private getValClass(val: number): string {
		if (val > 0) { return 'stat-bullish'; }
		if (val < 0) { return 'stat-bearish'; }
		return 'stat-neutral';
	}

	// SVG Icons
	private getTrendIcon() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>'; }
	private getMomIcon() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>'; }
	private getStrIcon() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>'; }
	private getVolIcon() { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>'; }
}

export const scanner = new Scanner();
